import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import Header from '../../components/Header';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const TIP_COLORS = {
  YILLIK: { bg: '#fff3e0', color: '#e65100', label: 'Yıllık' },
  MAZERET: { bg: '#e3f2fd', color: '#1565c0', label: 'Mazeret' },
  RAPOR: { bg: '#fce4ec', color: '#c62828', label: 'Rapor' },
  UCRETSIZ: { bg: '#eceff1', color: '#455a64', label: 'Ücretsiz' },
};

const th = { padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#555', borderBottom: '2px solid #ddd' };
const td = { padding: '6px 8px', fontSize: 11 };
const btnSmall = { padding: '4px 10px', fontSize: 11, border: '1px solid #ccc', background: 'white', borderRadius: 3, cursor: 'pointer' };

const IzinKaydiPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_HR, user.effectivePermissions);

  const [yil, setYil] = useUrlState('yil', String(new Date().getFullYear()));
  const [tipFilter, setTipFilter] = useUrlState('tip', '');
  const [personelFilter, setPersonelFilter] = useState('');
  const [izinler, setIzinler] = useState([]);
  const [personeller, setPersoneller] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const token = localStorage.getItem('access_token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('yil', yil);
      if (tipFilter) qs.set('tip', tipFilter);
      if (personelFilter) qs.set('personel_id', personelFilter);

      const headers = { 'Authorization': `Bearer ${token}` };
      const [izRes, persRes] = await Promise.all([
        fetch(`${API_URL}/api/izin?${qs}`, { headers }),
        fetch(`${API_URL}/api/personel?aktif=true`, { headers }),
      ]);
      if (izRes.ok) setIzinler(await izRes.json());
      if (persRes.ok) setPersoneller(await persRes.json());
    } catch (e) { logError(e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yil, tipFilter, personelFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!personelFilter) { setOzet(null); return; }
    const headers = { 'Authorization': `Bearer ${token}` };
    fetch(`${API_URL}/api/izin/ozet/${personelFilter}?yil=${yil}`, { headers })
      .then(r => r.ok ? r.json() : null).then(setOzet).catch(() => setOzet(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personelFilter, yil]);

  const handleDelete = async (i) => {
    if (!window.confirm(`${i.personel_ad} · ${i.baslangic_tarihi} izni silinecek.`)) return;
    const headers = { 'Authorization': `Bearer ${token}` };
    const r = await fetch(`${API_URL}/api/izin/${i.id}`, { method: 'DELETE', headers });
    if (r.ok) { toast.success('Silindi'); fetchData(); }
    else toast.error('Silinemedi');
  };

  if (!canManage) {
    return <div><Header title="İzin Kaydı" /><div style={{ padding: 40, textAlign: 'center' }}>Yetkiniz yok.</div></div>;
  }

  return (
    <div>
      <Header title="İzin Kaydı" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Filtre bar */}
        <div style={{ background: colors.surface, padding: 14, borderRadius: radius.lg, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', boxShadow: shadows.sm }}>
          <input type="number" min="2024" max="2030" value={yil} onChange={e => setYil(parseInt(e.target.value, 10) || new Date().getFullYear())} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, width: 100 }} />
          <select value={personelFilter} onChange={e => setPersonelFilter(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, minWidth: 220 }}>
            <option value="">Tüm Personel</option>
            {personeller.map(p => <option key={p.id} value={p.id}>{p.ad_soyad}</option>)}
          </select>
          <select value={tipFilter} onChange={e => setTipFilter(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }}>
            <option value="">Tüm Tipler</option>
            <option value="YILLIK">Yıllık</option>
            <option value="MAZERET">Mazeret</option>
            <option value="RAPOR">Rapor</option>
            <option value="UCRETSIZ">Ücretsiz</option>
          </select>
          <span style={{ fontSize: 11, color: '#888' }}>{izinler.length} kayıt</span>
          <button onClick={() => { setEditing(null); setModalOpen(true); }} style={{ marginLeft: 'auto', padding: '9px 18px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            + Yeni İzin
          </button>
        </div>

        <div style={{ display: 'flex', gap: 14 }}>
          {/* Sol: Liste */}
          <div style={{ flex: ozet ? '0 0 68%' : 1, background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
            {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div>
             : izinler.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>İzin kaydı yok</div>
             : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={th}>Personel</th>
                    <th style={th}>Başlangıç</th>
                    <th style={th}>Bitiş</th>
                    <th style={{ ...th, textAlign: 'right' }}>Gün</th>
                    <th style={th}>Tip</th>
                    <th style={th}>Açıklama</th>
                    <th style={th}>Aksiyon</th>
                  </tr>
                </thead>
                <tbody>
                  {izinler.map(i => {
                    const tc = TIP_COLORS[i.tip] || TIP_COLORS.UCRETSIZ;
                    return (
                      <tr key={i.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={td}>{i.personel_ad}</td>
                        <td style={td}>{i.baslangic_tarihi}</td>
                        <td style={td}>{i.bitis_tarihi}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{i.gun_sayisi}</td>
                        <td style={td}>
                          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: tc.bg, color: tc.color }}>{tc.label}</span>
                        </td>
                        <td style={{ ...td, fontSize: 12, color: '#666', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.aciklama || ''}>{i.aciklama || '-'}</td>
                        <td style={td}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => { setEditing(i); setModalOpen(true); }} style={btnSmall}>Düzenle</button>
                            <button onClick={() => handleDelete(i)} style={{ ...btnSmall, background: '#f8d7da', color: '#721c24', borderColor: '#f5c6cb' }}>Sil</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Sağ: özet kart */}
          {ozet && (
            <div style={{ flex: '0 0 31%', background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, padding: 18, alignSelf: 'flex-start' }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: 16 }}>{ozet.personel_ad}</h3>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>
                Kıdem: {ozet.kidem_yil} yıl{ozet.ise_giris_tarihi ? ` · Giriş: ${ozet.ise_giris_tarihi}` : ''}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>Yıllık Hak</span><strong>{ozet.yillik_hak} gün</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>Kullanılan</span><strong>{ozet.yillik_kullanilan} gün</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: '#2e7d32', marginTop: 4 }}><span>Kalan</span><span>{ozet.yillik_kalan} gün</span></div>
                {ozet.yillik_hak > 0 && (
                  <div style={{ marginTop: 8, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, (ozet.yillik_kullanilan / ozet.yillik_hak) * 100)}%`, height: '100%', background: ozet.yillik_kullanilan >= ozet.yillik_hak ? '#c62828' : '#fbc02d' }} />
                  </div>
                )}
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '14px 0' }} />

              <div style={{ fontSize: 12, color: '#555', lineHeight: 1.8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Mazeret</span><strong>{ozet.mazeret} gün</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Rapor</span><strong>{ozet.rapor} gün</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Ücretsiz</span><strong>{ozet.ucretsiz} gün</strong></div>
              </div>
            </div>
          )}
        </div>

        {modalOpen && (
          <IzinModal
            izin={editing}
            personeller={personeller}
            onClose={() => { setModalOpen(false); setEditing(null); }}
            onSaved={() => { setModalOpen(false); setEditing(null); fetchData(); }}
          />
        )}
      </div>
    </div>
  );
};

const IzinModal = ({ izin, personeller, onClose, onSaved }) => {
  const toast = useToast();
  const editing = !!izin;
  const [form, setForm] = useState(() => izin ? ({
    personel_id: izin.personel_id,
    baslangic_tarihi: izin.baslangic_tarihi,
    bitis_tarihi: izin.bitis_tarihi,
    tip: izin.tip,
    gun_sayisi: izin.gun_sayisi,
    aciklama: izin.aciklama || '',
    _manual_gun: true,
  }) : ({
    personel_id: '',
    baslangic_tarihi: new Date().toISOString().split('T')[0],
    bitis_tarihi: new Date().toISOString().split('T')[0],
    tip: 'YILLIK',
    gun_sayisi: 1,
    aciklama: '',
    _manual_gun: false,
  }));
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('access_token');

  // Otomatik gün hesabı (manuel override edilmediyse)
  useEffect(() => {
    if (form._manual_gun) return;
    if (!form.baslangic_tarihi || !form.bitis_tarihi) return;
    const b = new Date(form.baslangic_tarihi);
    const bt = new Date(form.bitis_tarihi);
    const gun = Math.max(0, (bt - b) / (1000 * 60 * 60 * 24) + 1);
    setForm(f => ({ ...f, gun_sayisi: gun }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.baslangic_tarihi, form.bitis_tarihi]);

  const handleSave = async () => {
    if (!form.personel_id) { toast.warning('Personel seç'); return; }
    if (!form.baslangic_tarihi || !form.bitis_tarihi) { toast.warning('Tarih gerekli'); return; }
    setSaving(true);
    const payload = {
      personel_id: parseInt(form.personel_id, 10),
      baslangic_tarihi: form.baslangic_tarihi,
      bitis_tarihi: form.bitis_tarihi,
      tip: form.tip,
      aciklama: form.aciklama || null,
      gun_sayisi: parseFloat(form.gun_sayisi) || null,
    };
    const url = editing ? `${API_URL}/api/izin/${izin.id}` : `${API_URL}/api/izin`;
    const method = editing ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, { method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (r.ok) { toast.success(editing ? 'Güncellendi' : 'Eklendi'); onSaved(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Hata'); }
    } catch { toast.error('Bağlantı hatası'); }
    setSaving(false);
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 11, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, color: colors.textSecondary, fontWeight: 600, marginBottom: 4, marginTop: 10 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: colors.surface, padding: 24, borderRadius: radius.lg, width: 460, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px 0' }}>{editing ? 'İzin Düzenle' : 'Yeni İzin'}</h3>

        <label style={labelStyle}>Personel *</label>
        <select style={inputStyle} value={form.personel_id} onChange={e => setForm({ ...form, personel_id: e.target.value })}>
          <option value="">(Seçiniz)</option>
          {personeller.map(p => <option key={p.id} value={p.id}>{p.ad_soyad}</option>)}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Başlangıç *</label>
            <input style={inputStyle} type="date" value={form.baslangic_tarihi} onChange={e => setForm({ ...form, baslangic_tarihi: e.target.value, _manual_gun: false })} />
          </div>
          <div>
            <label style={labelStyle}>Bitiş *</label>
            <input style={inputStyle} type="date" value={form.bitis_tarihi} onChange={e => setForm({ ...form, bitis_tarihi: e.target.value, _manual_gun: false })} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Tip</label>
            <select style={inputStyle} value={form.tip} onChange={e => setForm({ ...form, tip: e.target.value })}>
              <option value="YILLIK">Yıllık</option>
              <option value="MAZERET">Mazeret</option>
              <option value="RAPOR">Rapor</option>
              <option value="UCRETSIZ">Ücretsiz</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Gün Sayısı (otomatik)</label>
            <input style={inputStyle} type="number" step="0.5" value={form.gun_sayisi} onChange={e => setForm({ ...form, gun_sayisi: e.target.value, _manual_gun: true })} />
          </div>
        </div>

        <label style={labelStyle}>Açıklama</label>
        <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }} value={form.aciklama} onChange={e => setForm({ ...form, aciklama: e.target.value })} />

        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: 10, background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>{saving ? 'Kaydediliyor...' : (editing ? 'Güncelle' : 'Kaydet')}</button>
          <button onClick={onClose} style={{ flex: 1, padding: 10, background: '#666', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>İptal</button>
        </div>
      </div>
    </div>
  );
};

export default IzinKaydiPage;
